/**
 * Route configuration for the reskinned dashboard experience
 *
 * This configuration drives the dynamic route generation in App.js
 * and provides a single source of truth for all route mappings.
 *
 * Each route object contains:
 * - path: The URL path segment (relative to parent route)
 * - view: The view name passed to WidgetDrawer component
 * - label: Human-readable label for navigation (optional)
 * - description: Brief description of the route's purpose (optional)
 */

export const reskinnedRoutes = [
  {
    path: 'overview',
    view: 'dashboard',
    label: 'Dashboard',
    description: 'Main dashboard overview with key metrics and insights',
  },
  {
    path: 'tasks',
    view: 'task',
    label: 'Tasks',
    description: 'Task management and tracking',
  },
  {
    path: 'cost',
    view: 'reskinnedCost',
    label: 'Cost',
    description: 'Cost analysis and financial insights',
  },
  {
    path: 'budget',
    view: 'budget',
    label: 'Budget',
    description: 'Budget planning and management',
  },
  {
    path: 'benchmark',
    view: 'benchmark',
    label: 'Benchmark',
    description: 'Performance benchmarking and comparisons',
  },
  {
    path: 'ai-summary',
    view: 'aiSummary',
    label: 'AI Summary',
    description: 'AI-generated insights and summaries',
  },
  {
    path: 'ai-ondemand',
    view: 'aiSummaryOD',
    label: 'AI On-Demand',
    description: 'On-demand AI analysis and reports',
  },
  {
    path: 'settings',
    view: 'setting',
    label: 'Settings',
    description: 'Application settings and preferences',
  },
  {
    path: 'integrations',
    view: 'integrationSettings',
    label: 'Integrations',
    description: 'Third-party integrations and connections',
  },
  {
    path: 'users',
    view: 'reskinnedManageUsers',
    label: 'Users',
    description: 'User management and permissions',
  },
  {
    path: 'profile',
    view: 'Profile',
    label: 'Profile',
    description: 'User profile and account settings',
  },
  {
    path: 'upload',
    view: 'UploadData',
    label: 'Upload',
    description: 'Data upload and import functionality',
  },
  {
    path: 'location-analysis',
    view: 'reskinnedLocationAnalysis',
    label: 'Location Analysis',
    description: 'Location-based performance analysis',
  },
  {
    path: 'comparison-overtime',
    view: 'reskinnedComparisonOvertime',
    label: 'Comparison Overtime',
    description: 'Time-based performance comparisons',
  },
  {
    path: 'account-alias',
    view: 'reskinnedAccountAlias',
    label: 'Account Alias',
    description: 'Account alias management',
  },
  {
    path: 'role-setting',
    view: 'reskinnedRoleSetting',
    label: 'Role Settings',
    description: 'User role definitions and permissions',
  },
  {
    path: 'privilege-settings',
    view: 'reskinnedPrivilegeSettings',
    label: 'Privilege Settings',
    description: 'User privilege and access control',
  },
  {
    path: 'exclusion-settings',
    view: 'reskinnedExclusionSettings',
    label: 'Exclusion Settings',
    description: 'Data exclusion rules and filters',
  },
  {
    path: 'budget-update',
    view: 'budgetUpdate',
    label: 'Budget Update',
    description: 'Budget modification and updates',
  },
  {
    path: 'budget-drill-down',
    view: 'reskinnedBudgetUpdateDD',
    label: 'Budget Drill Down',
    description: 'Detailed budget analysis and breakdown',
  },
  {
    path: 'subscription-billing',
    view: 'reskinnedSubscriptionBilling',
    label: 'Subscription & Billing',
    description: 'Self-serve subscription management and cancellation',
  },
];

/**
 * Helper function to create URL-to-view mapping from route configuration
 * @returns {Object} Mapping of URL paths to view names
 */
export const createUrlToViewMap = () => {
  return reskinnedRoutes.reduce((map, route) => {
    map[`/${route.path}`] = route.view;
    return map;
  }, {});
};

/**
 * Helper function to create view-to-URL mapping from route configuration
 * @returns {Object} Mapping of view names to URL paths
 */
export const createViewToUrlMap = () => {
  return reskinnedRoutes.reduce((map, route) => {
    map[route.view] = `/${route.path}`;
    return map;
  }, {});
};

/**
 * Get route configuration by path
 * @param {string} path - The route path to find
 * @returns {Object|undefined} Route configuration object or undefined if not found
 */
export const getRouteByPath = path => {
  return reskinnedRoutes.find(route => route.path === path);
};

/**
 * Get route configuration by view name
 * @param {string} view - The view name to find
 * @returns {Object|undefined} Route configuration object or undefined if not found
 */
export const getRouteByView = view => {
  return reskinnedRoutes.find(route => route.view === view);
};

/**
 * Get all available route paths
 * @returns {string[]} Array of all route paths
 */
export const getAllRoutePaths = () => {
  return reskinnedRoutes.map(route => route.path);
};

/**
 * Get all available view names
 * @returns {string[]} Array of all view names
 */
export const getAllViewNames = () => {
  return reskinnedRoutes.map(route => route.view);
};
